import { Component, ErrorInfo, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRouteError, useNavigate } from 'react-router-dom';
import {
  RefreshCw,
  ArrowLeft,
  Home,
  Mail,
  ExternalLink,
  Languages,
  AlertTriangle,
  X,
  Check,
} from 'lucide-react';
import { queryClient } from '../lib/queryClient';
import apiService from '../services/api';

const AUTO_REPORT_SECONDS = 5;
const ERROR_BANNER_Z_INDEX = 'z-10000';

interface ReportDetails {
  url: string;
  accountId: string;
  userEmail: string;
  timestamp: string;
  userAgent: string;
  language: string;
  screenResolution: string;
  errorMessage: string;
  stackTrace: string;
}

const getReportDetails = (error: Error | null): ReportDetails => {
  let accountId = 'N/A';
  let userEmail = 'N/A';
  try {
    const cachedSettings = queryClient.getQueryData<{
      accountId?: string;
      email?: string;
    }>(['userSettings']);
    if (cachedSettings) {
      accountId = cachedSettings.accountId || 'N/A';
      userEmail = cachedSettings.email || 'N/A';
    }
  } catch (e) {
    console.error('Failed to get cached userSettings in ErrorBoundary:', e);
  }

  return {
    url: window.location.href,
    accountId,
    userEmail,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    language: navigator.language,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    errorMessage: error?.toString() || 'Unknown Error',
    stackTrace: error?.stack || 'N/A',
  };
};

const formatReportText = (details: ReportDetails & { additionalDetails?: string }): string => {
  let bodyText = `Hi Support,

A page crash has occurred. Here are the details of the incident:

=== INCIDENT DETAILS ===
URL: ${details.url}
Account ID: ${details.accountId}
User Email: ${details.userEmail}
Timestamp: ${details.timestamp}
Language/Locale: ${details.language}
User Agent: ${details.userAgent}
Screen Resolution: ${details.screenResolution}`;

  if (details.additionalDetails) {
    bodyText += `\n\n=== USER DETAILS / ACTIONS ===\n${details.additionalDetails}`;
  }

  bodyText += `\n\n=== ERROR MESSAGE ===\n${details.errorMessage}\n\n=== STACK TRACE ===\n${details.stackTrace}`;
  return bodyText;
};

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
  resetKey?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  showDetails: boolean;
  isTranslationError: boolean;
  isReporting: boolean;
  reportingSuccess: boolean | null;
  additionalDetails: string;
}

const isTranslationRelatedError = (error: Error): boolean =>
  error.name === 'NotFoundError' &&
  (error.message?.includes('removeChild') || error.message?.includes('insertBefore'));

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    showDetails: false,
    isTranslationError: false,
    isReporting: false,
    reportingSuccess: null,
    additionalDetails: '',
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      showDetails: false,
      isTranslationError: isTranslationRelatedError(error),
      isReporting: false,
      reportingSuccess: null,
      additionalDetails: '',
    };
  }

  private handleReport = async () => {
    const { error, additionalDetails } = this.state;
    this.setState({ isReporting: true, reportingSuccess: null });
    try {
      const details = getReportDetails(error);
      await apiService.reportIssue({
        ...details,
        additionalDetails,
      });
      this.setState({ isReporting: false, reportingSuccess: true });
    } catch (err) {
      console.error('Failed to submit bug report:', err);
      this.setState({ isReporting: false, reportingSuccess: false });
    }
  };

  public componentDidUpdate(previous: Props) {
    // Recover after navigation without remounting a healthy editor and losing its draft.
    if (this.state.hasError && previous.resetKey !== this.props.resetKey) {
      this.setState({
        hasError: false,
        error: null,
        showDetails: false,
        isTranslationError: false,
        isReporting: false,
        reportingSuccess: null,
        additionalDetails: '',
      });
    }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <ErrorLayout
          isTranslationError={this.state.isTranslationError}
          error={this.state.error}
          additionalDetails={this.state.additionalDetails}
          setAdditionalDetails={(val) => this.setState({ additionalDetails: val })}
          isReporting={this.state.isReporting}
          reportingSuccess={this.state.reportingSuccess}
          handleReport={this.handleReport}
          showDetails={this.state.showDetails}
          setShowDetails={(val) => this.setState({ showDetails: val })}
        />
      );
    }

    return this.props.children;
  }
}

interface ErrorLayoutProps {
  isTranslationError: boolean;
  error: Error | null;
  additionalDetails: string;
  setAdditionalDetails: (val: string) => void;
  isReporting: boolean;
  reportingSuccess: boolean | null;
  handleReport: () => void;
  showDetails: boolean;
  setShowDetails: (val: boolean) => void;
}

const BTN_BASE =
  'inline-flex items-center justify-center h-8 w-8 sm:h-auto sm:w-auto px-0 sm:px-4 py-0 sm:py-2 text-xs rounded-full font-semibold transition-all shrink-0 disabled:opacity-50 disabled:cursor-not-allowed';

const BTN_SECONDARY =
  `${BTN_BASE} bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 dark:text-gray-200 dark:border-zinc-700`;

/** Fixed width so Cancel ↔ Send Report swap without layout shift */
const REPORT_SLOT_BTN =
  'inline-flex items-center justify-center h-8 w-8 sm:h-auto sm:w-34 px-0 sm:px-4 py-0 sm:py-2 text-xs rounded-full font-semibold transition-all shrink-0 disabled:opacity-50 disabled:cursor-not-allowed';

const BTN_LABEL = 'hidden sm:inline';
const BTN_ICON_SM = 'w-3.5 h-3.5 sm:mr-1.5';

function ErrorLayout({
  isTranslationError,
  error,
  additionalDetails,
  setAdditionalDetails,
  isReporting,
  reportingSuccess,
  handleReport,
  showDetails,
  setShowDetails,
}: ErrorLayoutProps) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [countdown, setCountdown] = useState(AUTO_REPORT_SECONDS);
  const [autoReportCancelled, setAutoReportCancelled] = useState(false);
  const autoReportTriggeredRef = useRef(false);

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const cancelAutoReport = useCallback(() => {
    setAutoReportCancelled(true);
  }, []);

  const onManualReport = useCallback(() => {
    setAutoReportCancelled(true);
    handleReport();
  }, [handleReport]);

  useEffect(() => {
    if (
      autoReportCancelled ||
      isReporting ||
      reportingSuccess !== null ||
      autoReportTriggeredRef.current
    ) {
      return;
    }

    if (countdown <= 0) {
      autoReportTriggeredRef.current = true;
      handleReport();
      return;
    }

    const timer = window.setTimeout(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [autoReportCancelled, countdown, handleReport, isReporting, reportingSuccess]);

  const wrapperClass = isTranslationError
    ? 'bg-yellow-100 dark:bg-yellow-950/90 border-yellow-300 dark:border-yellow-700'
    : 'bg-red-50 dark:bg-red-950/90 border-red-200 dark:border-red-800';

  const titleClass = isTranslationError
    ? 'text-yellow-950 dark:text-yellow-100'
    : 'text-red-900 dark:text-red-100';

  const textClass = isTranslationError
    ? 'text-yellow-900 dark:text-yellow-200'
    : 'text-red-800 dark:text-red-200';

  const reloadBtnClass = `${BTN_BASE} shadow-md group ${
    isTranslationError
      ? 'bg-yellow-500 hover:bg-yellow-600 text-zinc-900 shadow-yellow-500/30'
      : 'bg-cyan-600 hover:bg-cyan-700 text-zinc-100 shadow-cyan-500/25'
  }`;

  const showAutoCountdown =
    !autoReportCancelled && reportingSuccess === null && !isReporting && countdown > 0;

  const reportSlot = showAutoCountdown ? (
    <button
      type="button"
      onClick={cancelAutoReport}
      aria-label={`Cancel auto report, sending in ${countdown} seconds`}
      className={`${REPORT_SLOT_BTN} bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 dark:text-gray-200 dark:border-zinc-700 tabular-nums`}
    >
      <X className={`${BTN_ICON_SM} hidden sm:inline-block`} aria-hidden="true" />
      <span className="sm:hidden text-[11px] font-bold">{countdown}s</span>
      <span className="hidden sm:inline">
        Cancel · <strong>{countdown}s</strong>
      </span>
    </button>
  ) : reportingSuccess === true ? (
    <button
      type="button"
      disabled
      aria-label="Report sent"
      className={`${REPORT_SLOT_BTN} bg-green-600 text-zinc-100 shadow-green-500/25 cursor-default disabled:opacity-100`}
    >
      <Check className={BTN_ICON_SM} aria-hidden="true" />
      <span className={BTN_LABEL}>Reported</span>
    </button>
  ) : isReporting ? (
    <button
      type="button"
      disabled
      aria-label="Reporting"
      className={`${REPORT_SLOT_BTN} bg-green-600 text-zinc-100 shadow-green-500/25`}
    >
      <RefreshCw className={`${BTN_ICON_SM} animate-spin`} aria-hidden="true" />
      <span className={BTN_LABEL}>Reporting...</span>
    </button>
  ) : autoReportCancelled ? (
    <button
      type="button"
      onClick={onManualReport}
      aria-label={reportingSuccess === false ? 'Report Failed, Retry' : 'Send Report'}
      className={`${REPORT_SLOT_BTN} shadow-lg active:scale-95 ${
        reportingSuccess === false
          ? 'bg-green-500 text-zinc-100 shadow-green-500/25'
          : 'bg-green-600 hover:bg-green-700 text-zinc-100 shadow-green-500/25'
      }`}
    >
      <Mail className={BTN_ICON_SM} aria-hidden="true" />
      <span className={BTN_LABEL}>
        {reportingSuccess === false ? 'Retry Report' : 'Send Report'}
      </span>
    </button>
  ) : null;

  const banner = (
    <div
      className={`fixed top-0 left-0 right-0 ${ERROR_BANNER_Z_INDEX} border-b shadow-lg backdrop-blur-sm max-h-[min(100dvh,100vh)] overflow-y-auto pt-[env(safe-area-inset-top)] ${wrapperClass}`}
      role="alert"
      aria-live="assertive"
    >
      {/* ── Main banner bar ── */}
      <div className="px-3 sm:px-4 py-2.5">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2.5 max-w-7xl mx-auto">
          <div className="flex items-start gap-2 min-w-0 flex-1">
            {isTranslationError ? (
              <Languages className="h-4 w-4 shrink-0 mt-0.5 text-yellow-600 dark:text-yellow-400" aria-hidden="true" />
            ) : (
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-red-600 dark:text-red-400" aria-hidden="true" />
            )}
            <div className="min-w-0 flex-1">
              <p className={`text-xs font-semibold ${titleClass}`}>
                {isTranslationError ? 'Please do not translate this page' : 'This page hit an error'}
              </p>
              <p className={`text-xs mt-0.5 ${textClass}`}>
                <span className="sm:hidden">
                  {isTranslationError
                    ? 'Turn off Translate, then reload.'
                    : 'Reload or go back to retry.'}
                </span>
                <span className="hidden sm:inline">
                  {isTranslationError
                    ? 'Your browser’s Translate feature interrupted what you were doing. Turn it off for this site, then reload.'
                    : reportingSuccess === true
                      ? 'Reload to continue, or go back and retry your last action. Report sent to support.'
                      : 'Reload to continue, or go back and retry your last action. A report will send automatically.'}
                </span>
                {' '}
                <button
                  type="button"
                  onClick={() => setExpanded((prev) => !prev)}
                  className={`font-semibold underline underline-offset-2 hover:opacity-80 ${textClass}`}
                  aria-expanded={expanded}
                >
                  <span>{expanded ? 'Hide details' : 'Show details'}</span>
                </button>
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end sm:justify-start gap-1.5 sm:gap-2 shrink-0">
              <button
                type="button"
                onClick={handleGoBack}
                aria-label="Go Back"
                className={BTN_SECONDARY}
              >
                <ArrowLeft className={BTN_ICON_SM} aria-hidden="true" />
                <span className={BTN_LABEL}>Go Back</span>
              </button>
              {reportSlot}
              <button
                type="button"
                onClick={() => window.location.reload()}
                aria-label="Reload Page"
                className={reloadBtnClass}
              >
                <RefreshCw className={`${BTN_ICON_SM} group-hover:rotate-180 transition-transform duration-500`} aria-hidden="true" />
                <span className={BTN_LABEL}>Reload Page</span>
              </button>
          </div>
        </div>
      </div>

      {/* ── Expanded details ── */}
      {expanded && (
        <div className="px-3 sm:px-4 pb-4 border-t bg-white dark:bg-black border-zinc-200 dark:border-zinc-800">
          <div className="max-w-7xl mx-auto pt-3 space-y-4">
            <p className="text-xs leading-relaxed text-zinc-700 dark:text-zinc-300">
              {isTranslationError
                ? 'When Translate is on, your browser can change the page while you\'re using it, which sometimes causes this glitch. Turn Translate off, reload, and try again.'
                : 'Your last action may not have finished. Use Reload to restore the page, or Go Back and try again. If it keeps happening, add a short note below and send a report.'}
            </p>

            {isTranslationError && (
              <div className="text-left bg-yellow-50 dark:bg-yellow-950/40 border border-yellow-300 dark:border-yellow-700 rounded-xl p-3 sm:p-4 text-xs text-yellow-900 dark:text-yellow-200 space-y-2">
                <p className="font-semibold text-yellow-950 dark:text-yellow-100 flex items-center gap-1.5">
                  <span>💡</span> How to fix it
                </p>
                <p>
                  Turn off <strong>Translate</strong> for this site while you finish your work. You can turn it back on afterwards.
                </p>
                <ul className="space-y-1 pl-3 list-disc list-outside text-yellow-800 dark:text-yellow-300">
                  <li><strong>Chrome / Edge:</strong> Click the translate icon in the address bar → choose <em>&quot;Never translate this site&quot;</em> or <em>&quot;Stop translating&quot;</em>.</li>
                  <li><strong>Firefox:</strong> Click the translate icon in the toolbar → choose <em>&quot;Never translate this site&quot;</em>.</li>
                  <li><strong>Safari:</strong> Open the View menu → Translation → choose <em>&quot;Don&apos;t Translate&quot;</em>.</li>
                </ul>
              </div>
            )}

            <div className="text-left w-full">
              <label className="block text-xs mb-1.5 text-zinc-700 dark:text-zinc-300">
                Please provide any additional context or steps that led to this error (optional):
              </label>
              <textarea
                value={additionalDetails}
                onChange={(e) => setAdditionalDetails(e.target.value)}
                placeholder="e.g. Trying to send a test email to a new contact. The error occurred when I clicked on the send button."
                rows={3}
                className="w-full p-3 bg-gray-50 border border-gray-200 text-zinc-900 placeholder-zinc-400 dark:bg-zinc-900 dark:border-zinc-700 dark:text-zinc-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 text-xs resize-none"
              />
            </div>

            <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center sm:justify-between gap-3">
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-4">
                <a href="/" className="flex items-center text-xs text-zinc-500 hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors">
                  <Home className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
                  <span>Return Home</span>
                </a>
                <a
                  href="https://www.simplysend.email/contact"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-xs text-zinc-500 hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors"
                >
                  <Mail className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
                  <span>Contact Support</span>
                  <ExternalLink className="w-3.5 h-3.5 ml-1" aria-hidden="true" />
                </a>
              </div>
              {error && (
                <button
                  type="button"
                  onClick={() => setShowDetails(!showDetails)}
                  className="text-xs text-zinc-950 dark:text-zinc-100 hover:underline font-semibold sm:ml-auto"
                >
                  <span>{showDetails ? 'Hide Report Data' : 'Show Report Data'}</span>
                </button>
              )}
            </div>

            {error && showDetails && (() => {
              const details = getReportDetails(error);
              const reportText = formatReportText({ ...details, additionalDetails });
              return (
                <div className="p-3 sm:p-4 bg-gray-50 dark:bg-zinc-900 rounded-lg text-left border border-zinc-200 dark:border-zinc-800">
                  <div className="text-[10px] font-bold text-gray-400 dark:text-gray-500 mb-2 uppercase tracking-wider">
                    Preview of report data sent to support:
                  </div>
                  <pre className="text-xs font-mono text-zinc-800 dark:text-zinc-200 whitespace-pre-wrap break-all max-h-48 sm:max-h-60 overflow-y-auto">
                    {reportText}
                  </pre>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(banner, document.body)
    : banner;
}

export default ErrorBoundary;

/**
 * React Router route-level error page.
 * React Router renders this via `errorElement` when a route or its children
 * throw during render. It uses `useRouteError()` to retrieve the error and
 * renders the same styled error UI as the class-based ErrorBoundary.
 */
export function RouteErrorPage() {
  const error = useRouteError() as Error | null;
  const isTranslationError = !!error && isTranslationRelatedError(error);
  const [showDetails, setShowDetails] = useState(false);
  const [isReporting, setIsReporting] = useState(false);
  const [reportingSuccess, setReportingSuccess] = useState<boolean | null>(null);
  const [additionalDetails, setAdditionalDetails] = useState('');

  const handleReport = useCallback(async () => {
    setIsReporting(true);
    setReportingSuccess(null);
    try {
      const details = getReportDetails(error);
      await apiService.reportIssue({
        ...details,
        additionalDetails,
      });
      setReportingSuccess(true);
    } catch (err) {
      console.error('Failed to submit bug report:', err);
      setReportingSuccess(false);
    } finally {
      setIsReporting(false);
    }
  }, [error, additionalDetails]);

  return (
    <ErrorLayout
      isTranslationError={isTranslationError}
      error={error}
      additionalDetails={additionalDetails}
      setAdditionalDetails={setAdditionalDetails}
      isReporting={isReporting}
      reportingSuccess={reportingSuccess}
      handleReport={handleReport}
      showDetails={showDetails}
      setShowDetails={setShowDetails}
    />
  );
}
